import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { SwalService } from '@shared/services/swal.service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import {
  DELETE_BANK_DETAILS,
  LIST_ALL_BANK_DETAILS,
  SAVE_BANK_DETAILS,
} from '@modules/settings/settings.graphql';
import {
  DeleteBankDetailsResponse,
  ListAllBankDetailsResponse,
  SaveBankDetailsResponse,
} from '@modules/settings/types/bank-details';
import { BankDetailsActions } from './bank-details.actions';

@Injectable()
export class BankDetailsEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphqlService = inject(GraphQlService);
  private readonly swalService = inject(SwalService);

  loadAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BankDetailsActions.loadAll),
      switchMap(() =>
        this.graphqlService
          .query<ListAllBankDetailsResponse>({
            query: LIST_ALL_BANK_DETAILS,
            apolloNamespace: ApolloNamespace.billing,
          })
          .pipe(
            map((response) =>
              BankDetailsActions.loadAllSuccess({
                bankDetails: response.data?.listAllBankDetails ?? [],
              })
            ),
            catchError((error) => of(BankDetailsActions.loadAllFailure({ error })))
          )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BankDetailsActions.create),
      mergeMap(({ input }) =>
        this.graphqlService
          .mutate<SaveBankDetailsResponse>({
            mutation: SAVE_BANK_DETAILS,
            apolloNamespace: ApolloNamespace.billing,
            variables: { bankDetailsDto: input },
          })
          .pipe(
            map((response) => {
              const res = response.data?.saveBankDetails;
              if (res?.status) return BankDetailsActions.createSuccess({ entity: res.data });
              return BankDetailsActions.createFailure({ error: res?.message ?? 'Failed to save bank details' });
            }),
            catchError((error) => of(BankDetailsActions.createFailure({ error })))
          )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BankDetailsActions.update),
      mergeMap(({ input }) =>
        this.graphqlService
          .mutate<SaveBankDetailsResponse>({
            mutation: SAVE_BANK_DETAILS,
            apolloNamespace: ApolloNamespace.billing,
            variables: { bankDetailsDto: input },
          })
          .pipe(
            map((response) => {
              const res = response.data?.saveBankDetails;
              if (res?.status) return BankDetailsActions.updateSuccess({ entity: res.data });
              return BankDetailsActions.updateFailure({ error: res?.message ?? 'Failed to update bank details' });
            }),
            catchError((error) => of(BankDetailsActions.updateFailure({ error })))
          )
      )
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BankDetailsActions.delete),
      mergeMap(({ uid }) =>
        this.graphqlService
          .mutate<DeleteBankDetailsResponse>({
            mutation: DELETE_BANK_DETAILS,
            apolloNamespace: ApolloNamespace.billing,
            variables: { uid },
          })
          .pipe(
            map((response) => {
              const res = response.data?.deleteBankDetails;
              if (res?.status) return BankDetailsActions.deleteSuccess({ uid });
              return BankDetailsActions.deleteFailure({ error: res?.message ?? 'Failed to delete bank details' });
            }),
            catchError((error) => of(BankDetailsActions.deleteFailure({ error })))
          )
      )
    )
  );

  notifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          BankDetailsActions.createSuccess,
          BankDetailsActions.updateSuccess,
          BankDetailsActions.deleteSuccess,
          BankDetailsActions.createFailure,
          BankDetailsActions.updateFailure,
          BankDetailsActions.deleteFailure
        ),
        tap((action) => {
          if (action.type === BankDetailsActions.createSuccess.type) {
            this.swalService.successToast('Bank details saved successfully');
          } else if (action.type === BankDetailsActions.updateSuccess.type) {
            this.swalService.successToast('Bank details updated successfully');
          } else if (action.type === BankDetailsActions.deleteSuccess.type) {
            this.swalService.successToast('Bank details deleted successfully');
          } else if (
            action.type === BankDetailsActions.createFailure.type ||
            action.type === BankDetailsActions.updateFailure.type ||
            action.type === BankDetailsActions.deleteFailure.type
          ) {
            const anyAction: any = action;
            this.swalService.errorToast(anyAction.error?.toString?.() ?? 'Operation failed');
          }
        })
      ),
    { dispatch: false }
  );
}
